import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Feather, Heart, Ban, BookOpen, 
  Users, MessageCircle, AlertTriangle, CheckCircle
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const CommunityGuidelinesPage = () => {
  return (
    <div className="min-h-screen" data-testid="community-guidelines-page">
      {/* Header */}
      <section className="bg-gradient-to-br from-brand-primary/10 via-brand-gold/5 to-brand-accent/10 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-12 h-12 text-brand-primary mx-auto mb-4" />
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-text-primary mb-4">
            Community Guidelines
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Our simple rules to keep Collectively Write a creative, respectful, 
            and inspiring space for all writers.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        {/* Original Work */}
        <Card className="border-brand-primary/10 overflow-hidden" data-testid="guideline-original-work">
          <CardContent className="p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                <Feather className="w-7 h-7 text-brand-gold" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-3">
                  Original Writing Only
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  All work submitted to Collectively Write must be your own original creation. 
                  We celebrate the human craft of writing—AI-generated content is not permitted. 
                  This is a space for real writers honing real skills together.
                </p>
                <div className="flex items-center gap-2 text-sm text-brand-primary font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Your original stories, poems, and creative work
                </div>
                <div className="flex items-center gap-2 text-sm text-red-500 font-medium mt-1">
                  <Ban className="w-4 h-4" />
                  AI-generated or plagiarized content
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Writer Ownership */}
        <Card className="border-brand-primary/10 overflow-hidden" data-testid="guideline-ownership">
          <CardContent className="p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-7 h-7 text-brand-primary" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-3">
                  Writers Own 100% of Their Work
                </h2>
                <p className="text-text-secondary leading-relaxed">
                  Collectively Write claims absolutely zero rights to anything you create. 
                  You retain full copyright ownership over every word you write. 
                  You may remove your work at any time, no questions asked. 
                  Your portfolio is yours—always.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Rules */}
        <Card className="border-brand-primary/10 overflow-hidden" data-testid="guideline-content-rules">
          <CardContent className="p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-7 h-7 text-brand-accent" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-3">
                  Content We Steer Clear Of
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We welcome nearly every genre and voice. However, to keep our community 
                  focused on craft rather than controversy, we currently do not accept:
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-surface-alt rounded-lg text-center">
                    <Ban className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="font-medium text-text-primary text-sm">No Erotica</p>
                  </div>
                  <div className="p-4 bg-surface-alt rounded-lg text-center">
                    <Ban className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="font-medium text-text-primary text-sm">No Politics</p>
                  </div>
                  <div className="p-4 bg-surface-alt rounded-lg text-center">
                    <Ban className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="font-medium text-text-primary text-sm">No Preachy Content</p>
                  </div>
                </div>
                <p className="text-sm text-text-muted mt-4">
                  Faith-based stories are welcome as long as they're story-focused rather than preachy.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reviewing Guidelines */}
        <Card className="border-brand-primary/10 overflow-hidden" data-testid="guideline-feedback">
          <CardContent className="p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-7 h-7 text-brand-primary" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-4">
                  Reviewing Others' Work
                </h2>
                <p className="text-text-secondary leading-relaxed mb-5">
                  As writers, we understand, probably better than anyone, the power of the written word. 
                  So, when you're reviewing a piece of writing that a fellow member has shared, 
                  be sure to take into careful account the impact of what you're saying.
                </p>

                <div className="space-y-5">
                  {/* Candid without cruel */}
                  <div className="p-5 bg-surface-alt rounded-xl">
                    <p className="font-medium text-text-primary mb-3">
                      You can be candid without being cruel or sanctimonious.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary italic">
                          "I'm thinking this needs a stronger action verb here."
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Ban className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary italic">
                          "This word is too weak. You can do better."
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Complimentary but specific */}
                  <div className="p-5 bg-surface-alt rounded-xl">
                    <p className="font-medium text-text-primary mb-3">
                      You can be complimentary but specific.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-2">
                        <Ban className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary italic">
                          "This story made me feel happy."
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-secondary italic">
                          "I loved the way your protagonist said, '________.' Perfect word choice there!"
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-text-secondary leading-relaxed mt-5 font-medium">
                  Specific examples of what "landed" with you as you read someone's piece is the 
                  Gold Standard we're trying to achieve here. That's how, as writers, we'll know 
                  if we've conveyed what we intended.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* No Clique Voting */}
        <Card className="border-brand-primary/10 overflow-hidden" data-testid="guideline-fair-play">
          <CardContent className="p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-7 h-7 text-brand-gold" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-3">
                  Fair Play — No Clique Voting
                </h2>
                <p className="text-text-secondary leading-relaxed">
                  Challenges are judged on craft—best character development, best hook, 
                  best conceptualization. There are no popularity contests here. 
                  Coordinated voting or gaming any system will result in removal. 
                  Quality writing wins. Period.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Community Spirit */}
        <Card className="border-brand-primary/10 overflow-hidden" data-testid="guideline-community">
          <CardContent className="p-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-brand-accent" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-3">
                  Be a Good Community Member
                </h2>
                <p className="text-text-secondary leading-relaxed">
                  Read others' work. Leave thoughtful comments. Engage in discussions. 
                  Celebrate each other's wins. The best writing communities are built 
                  on generosity—give as much as you hope to receive.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center pt-6">
          <p className="text-text-secondary mb-6">
            Questions or feedback about these guidelines? We're always listening.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register" data-testid="guidelines-join-button">
              <Button className="btn-accent gap-2">
                <BookOpen className="w-4 h-4" />
                Join Our Community
              </Button>
            </Link>
            <Link to="/stories">
              <Button variant="outline" className="btn-outline gap-2">
                Browse Stories
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelinesPage;
